import React, { useCallback, useEffect, useRef } from "react";
import { Renderer, Stave, StaveNote, Voice, Formatter, Accidental } from "vexflow";
import type { PracticeNoteEvent } from "../../lib/practiceNoteModel";
import type { NoteName } from "../../lib/guitarEngine";
import {
  PRACTICE_STAFF_CHORD_SCALE,
  PRACTICE_STAFF_HEIGHT_CHORD,
  PRACTICE_STAFF_MIN_CHORD,
} from "../../lib/practiceStaffLayout";
import {
  PRACTICE_STAFF_CONTAINER_STYLE,
  notationNoteStyle,
  paintStaveLines,
} from "../../lib/practiceStaffTheme";

export interface ChordCompactStaffProps {
  events: PracticeNoteEvent[];
  chordLabel: string;
  degreeLine?: string;
  activeMidi?: number | null;
  className?: string;
}

const SCALE = PRACTICE_STAFF_CHORD_SCALE;

function buildVexKey(note: NoteName, octave: number): string {
  const baseName = note.replace("#", "").replace("b", "").toLowerCase();
  return `${baseName}/${octave}`;
}

export const ChordCompactStaff: React.FC<ChordCompactStaffProps> = ({
  events,
  chordLabel,
  degreeLine,
  activeMidi = null,
  className = "",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgHostRef = useRef<HTMLDivElement>(null);

  const renderScore = useCallback(() => {
    const host = svgHostRef.current;
    if (!host || events.length === 0) return;
    host.innerHTML = "";

    const width = host.clientWidth || containerRef.current?.clientWidth || 480;
    const height = PRACTICE_STAFF_HEIGHT_CHORD;
    const renderer = new Renderer(host, Renderer.Backends.SVG);
    renderer.resize(width, height);
    const ctx = renderer.getContext();
    ctx.scale(SCALE, SCALE);

    const pad = 24;
    const staveW = (width - pad * 2) / SCALE;
    const stave = new Stave(pad / SCALE, 8, staveW);
    stave.addClef("treble");
    paintStaveLines(ctx);
    stave.setContext(ctx).draw();

    const keys = events.map((ev) => {
      const octave = Math.floor(ev.midi / 12) - 1;
      const step = ev.step ?? ("C" as NoteName);
      return buildVexKey(step, octave);
    });

    const chordNote = new StaveNote({
      keys,
      duration: "w",
      autoStem: false,
    });

    events.forEach((ev, i) => {
      const step = ev.step ?? ("C" as NoteName);
      if (step.includes("#")) chordNote.addModifier(new Accidental("#"), i);
      else if (step.includes("b") && step.length > 1) chordNote.addModifier(new Accidental("b"), i);
    });

    chordNote.setStyle(notationNoteStyle({ active: activeMidi != null }));

    const voice = new Voice({ numBeats: 4, beatValue: 4 });
    voice.addTickables([chordNote]);
    new Formatter().joinVoices([voice]).format([voice], staveW - 40);
    chordNote.setStave(stave);
    voice.draw(ctx, stave);
  }, [events, activeMidi]);

  useEffect(() => {
    renderScore();
  }, [renderScore]);

  useEffect(() => {
    const host = svgHostRef.current;
    if (!host) return;
    const ro = new ResizeObserver(() => renderScore());
    ro.observe(host);
    return () => ro.disconnect();
  }, [renderScore]);

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg ${className}`}
      style={{
        ...PRACTICE_STAFF_CONTAINER_STYLE,
        minHeight: PRACTICE_STAFF_MIN_CHORD,
        height: PRACTICE_STAFF_HEIGHT_CHORD,
      }}
    >
      <div className="absolute top-1.5 left-3 z-[2] pointer-events-none">
        <p className="text-[11px] font-serif font-bold italic text-slate-200">{chordLabel}</p>
        {degreeLine && (
          <p className="text-[9px] font-sans text-slate-400 mt-0.5">{degreeLine}</p>
        )}
      </div>
      <div ref={containerRef} className="w-full flex-1 min-h-0">
        <div ref={svgHostRef} className="w-full" style={{ height: PRACTICE_STAFF_HEIGHT_CHORD }} />
      </div>
      <p className="absolute bottom-1 right-2 text-[8px] font-sans text-slate-500 pointer-events-none">
        Acorde en partitura
      </p>
    </div>
  );
};
